import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import {
    doc,
    getDoc,
    collection,
    getDocs,
    query,
    orderBy,
    limit,
    startAfter,
    where,
    onSnapshot,
} from "firebase/firestore";
import { db } from "./firebase";
import { THEME_COLOR, CLUB_ID, CLUB_NAME } from "./config/club"; // Importing your global theme color

import PlayerHeaderCard from "./components/player/PlayerHeaderCard";
import PlayerStatsCards from "./components/player/PlayerStatsCards";
import EditProfileModal from "./components/player/EditProfileModal";

import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import BottomNav from "./components/layout/BottomNav";
import AppHeader from "./components/layout/AppHeader";

import SessionList from "./components/player/SessionList";
import { QRCodeCanvas } from "qrcode.react";

export default function PlayerProfile() {
    const location = useLocation();
    const { from, clubId, sessionId } = location.state || {};
    const { username } = useParams();
    const navigate = useNavigate();
    const { user, setUser, logout } = useAuth();
    const [profile, setProfile] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [editing, setEditing] = useState(false);
    const [resolvedUid, setResolvedUid] = useState(null);

    const [showQr, setShowQr] = useState(false);


    const [pastJoinedSessions, setPastJoinedSessions] = useState([]);
    const [sessionStats, setSessionStats] = useState({}); // { sessionId: { w: 0, l: 0 } }

    const [h2hStats, setH2hStats] = useState({
        vsWins: 0,
        vsLosses: 0,
        pairedWins: 0,
        pairedLosses: 0
    });

    const THEME_VIOLET = THEME_COLOR;

    const loadUser = async () => {
        try {
            // 1️⃣ Resolve username → uid
            const usernameSnap = await getDoc(
                doc(db, "usernames", username)
            );
            if (!usernameSnap.exists()) {
                setNotFound(true);
                return;
            }

            const uid = usernameSnap.data().uid;
            setResolvedUid(uid);

            // 2️⃣ Load user + clubStats in parallel
            const [userSnap, statsSnap] = await Promise.all([
                getDoc(doc(db, "users", uid)),
                getDoc(doc(db, "users", uid, "clubStats", CLUB_ID))
            ]);

            if (!userSnap.exists()) {
                setNotFound(true);
                return;
            }

            const userData = userSnap.data();

            // 3️⃣ Set profile
            setProfile({
                uid,
                username,
                level: userData.level || 1,
                dupr: userData.dupr || null,
                displayName: userData.displayName || "Unknown Player",
                photoURL: userData.photoURL || "/img/rrc2.png",
                coverPhoto: userData.coverPhoto || "/img/bg.png",
                bio: userData.bio || null,
            });

            // 4️⃣ Set club stats
            setStats(
                statsSnap.exists()
                    ? statsSnap.data()
                    : { gamesPlayed: 0, wins: 0, losses: 0 }
            );

        } catch (err) {
            //console.log(err);
            console.error(err);
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => { loadUser(); }, [username]);

    useEffect(() => {
        if (!resolvedUid || !CLUB_ID) return;

        const q = query(
            collection(db, "clubs", CLUB_ID, "sessions"),
            where("playerUids", "array-contains", resolvedUid),
            where("status", "==", "ended")
        );

        const unsubscribe = onSnapshot(q, async (snap) => {
            const sessionsData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setPastJoinedSessions(sessionsData);

            const statsMap = {};

            await Promise.all(sessionsData.map(async (session) => {
                try {
                    const summaryRef = doc(db, "clubs", CLUB_ID, "sessions", session.id, "summary", "final");
                    const summarySnap = await getDoc(summaryRef);

                    if (summarySnap.exists()) {
                        const data = summarySnap.data();
                        const playersList = data.players || [];

                        // 1. Apply your exact sorting rules
                        const sorted = [...playersList].sort((a, b) => {
                            if (b.wins !== a.wins) return b.wins - a.wins;
                            if (b.winRate !== a.winRate) return b.winRate - a.winRate;
                            if (b.gamesPlayed !== a.gamesPlayed) return b.gamesPlayed - a.gamesPlayed;
                            return a.losses - b.losses;
                        });

                        // 2. Apply tie handling to find the rank
                        let lastRank = 0;
                        let lastStats = null;
                        let finalUserRank = null;
                        let userW = 0;
                        let userL = 0;

                        for (let i = 0; i < sorted.length; i++) {
                            const p = sorted[i];
                            const isTie = lastStats &&
                                p.wins === lastStats.wins &&
                                p.winRate === lastStats.winRate &&
                                p.gamesPlayed === lastStats.gamesPlayed &&
                                p.losses === lastStats.losses;

                            const currentRank = isTie ? lastRank : i + 1;

                            if (p.playerId === resolvedUid) {
                                finalUserRank = currentRank;
                                userW = p.wins || 0;
                                userL = p.losses || 0;
                                break;
                            }

                            lastRank = currentRank;
                            lastStats = p;
                        }

                        statsMap[session.id] = {
                            w: userW,
                            l: userL,
                            rank: finalUserRank
                        };
                    }
                } catch (err) {
                    console.error("Error calculating rank for session:", session.id, err);
                }
            }));

            setSessionStats(statsMap);
        });

        return () => unsubscribe();
    }, [resolvedUid]);


    const winRate = stats?.gamesPlayed ? Math.round((stats.wins / stats.gamesPlayed) * 100) : 0;

    const handleShare = async () => {
        const shareData = {
            title: `${profile?.displayName} - ${CLUB_NAME}`,
            text: `Check out my player stats at ${CLUB_NAME}!`,
            url: window.location.href,
        };

        try {
            if (navigator.share) {
                await navigator.share(shareData);
            } else {
                // Fallback: Copy to clipboard
                await navigator.clipboard.writeText(window.location.href);
                Swal.fire({
                    toast: true,
                    position: 'center',
                    icon: 'success',
                    title: 'Link copied to clipboard!',
                    showConfirmButton: false,
                    timer: 2000
                });
            }
        } catch (err) {
            console.log("Share failed", err);
        }
    };

    //for session list
    useEffect(() => {
        if (!resolvedUid || !CLUB_ID) return;

        const q = query(
            collection(db, "clubs", CLUB_ID, "sessions"),
            where("playerUids", "array-contains", resolvedUid),
            where("status", "==", "ended")
        );

        return onSnapshot(q, (snap) => {
            setPastJoinedSessions(
                snap.docs.map((d) => ({ id: d.id, ...d.data() }))
            );
        });
    }, [resolvedUid, clubId]);
    //console.log(pastJoinedSessions)


    //h2h
    const loadHeadToHead = async () => {

    if (!user || !resolvedUid) return;
    if (user.uid === resolvedUid) return;

    try {

        const snap = await getDocs(
            query(
                collection(db, "clubs", CLUB_ID, "matchHistory"),
                where("playerIds", "array-contains", resolvedUid)
            )
        );

        let vsWins = 0;
        let vsLosses = 0;
        let pairedWins = 0;
        let pairedLosses = 0;

        snap.forEach(doc => {

            const m = doc.data();

            if (!m.playerIds?.includes(user.uid)) return;

            const p = m.players || [];
            if (p.length !== 4 || !m.score) return;

            const [a, b] = m.score.split("-").map(Number);
            const teamAWon = a > b;

            const teamA = [p[0].id, p[1].id];
            const teamB = [p[2].id, p[3].id];

            const viewerTeam = teamA.includes(user.uid) ? "A" : "B";
            const profileTeam = teamA.includes(resolvedUid) ? "A" : "B";

            const viewerWon =
                (viewerTeam === "A" && teamAWon) ||
                (viewerTeam === "B" && !teamAWon);

            // 🤝 Same team
            if (viewerTeam === profileTeam) {

                viewerWon ? pairedWins++ : pairedLosses++;

            } 
            // ⚔️ Opponents
            else {

                viewerWon ? vsWins++ : vsLosses++;

            }

        });

        setH2hStats({
            vsWins,
            vsLosses,
            pairedWins,
            pairedLosses
        });

    } catch (err) {
        console.error(err);
    }

};

    useEffect(() => {
        if (resolvedUid && user) {
            loadHeadToHead();
        }
    }, [resolvedUid, user]);

    const vsGames = h2hStats.vsWins + h2hStats.vsLosses;
    const pairedGames = h2hStats.pairedWins + h2hStats.pairedLosses;

    const vsWinRate = vsGames
        ? Math.round((h2hStats.vsWins / vsGames) * 100)
        : 0;

    const pairedWinRate = pairedGames
        ? Math.round((h2hStats.pairedWins / pairedGames) * 100)
        : 0;

    //end h2h


    if (loading) return <div className="vh-100 d-flex align-items-center justify-content-center"><div className="spinner-border" style={{ color: THEME_VIOLET }} /></div>;
    //console.log(`/join?club=${clubId}&session=${sessionId}`)
    if (notFound) {
        //console.log(notFound)
        return (
            <div className="vh-100 d-flex flex-column justify-content-center align-items-center text-center px-4">
                {/* Branded Icon with soft background */}
                <div style={{ width: '80px', height: '80px' }}
                    className="bg-light rounded-circle d-flex align-items-center justify-content-center mb-4">
                    <i className="bi bi-person-x text-muted" style={{ fontSize: '2rem' }}></i>
                </div>

                {/* Branded Typography */}
                <h5 className="fw-black text-uppercase tracking-widest mb-2">
                    Player Not Found
                </h5>

                <p className="text-muted small mb-4" style={{ maxWidth: '250px' }}>
                    This profile doesn't exist or may have been moved.
                </p>

                {/* Consistent Button Style */}
                <button
                    className="btn btn-outline-secondary rounded-pill px-4 fw-bold shadow-sm"
                    onClick={() => navigate(-1)}
                    style={{
                        fontSize: '0.85rem'
                    }}
                >
                    <i className="bi bi-arrow-left me-2"></i>
                    Go Back
                </button>
            </div>
        );
    }
    const isOwnProfile = user?.uid === resolvedUid;

    return (
        <div className="min-vh-100 pb-5" style={{ marginBottom: 30 }}>

            {/* SLIM VIOLET HEADER (Exactly matches JoinClub) */}
            {user ? (
                <AppHeader
                    user={user}
                    title={"Player Profile"}
                    subtitle={"Stats & History"}
                    clubId={CLUB_ID}
                    sessionId={sessionId}
                    logout={logout}
                />
            ) : (
                <div
                    className="text-white py-3 shadow-sm mb-4"
                    style={{ background: `linear-gradient(135deg, ${THEME_VIOLET} 0%, #3a0a61 100%)` }}
                >
                    <div className="container d-flex align-items-center justify-content-between" style={{ maxWidth: 450 }}>
                        <div className="d-flex align-items-center">
                            <img src="/img/rrc.png" alt="Logo" className="me-2" style={{ maxHeight: "55px" }} />
                            <div>
                                <h6 className="fw-bold mb-0">Player Profile</h6>
                                <p className="mb-0 small opacity-75">{CLUB_NAME}</p>
                            </div>
                        </div>

                        <div className="d-flex gap-2">
                            {/* SHARE BUTTON */}
                            <button
                                className="btn btn-sm btn-outline-light rounded-pill px-2"
                                onClick={handleShare}
                                title="Share Profile"
                            >
                                <i className="bi bi-share-fill px-1"></i>
                            </button>


                            {from === "join" && clubId && sessionId ? (

                                <button className="btn btn-sm btn-outline-light rounded-pill px-3"
                                    onClick={() => {
                                        if (from === "join" && clubId && sessionId) {
                                            navigate(`/join?club=${clubId}&session=${sessionId}`, { replace: true });
                                        } else {
                                            navigate(-1);
                                        }
                                    }}>
                                    Back <i className="bi bi-arrow-left me-1"></i>
                                </button>
                            ) : (
                                <button className="btn btn-sm btn-outline-light rounded-pill px-3"
                                    onClick={() => {
                                        navigate('/join');
                                    }}>
                                    Join <i className="bi bi-qr-code me-1"></i>
                                </button>
                            )
                            }

                        </div>
                    </div>
                </div>
            )}


            {/* header end */}
            <div className="container px-4" style={{ maxWidth: 450, marginTop: 10 }}>

                {/* 1. Header Card (DisplayName & Avatar) */}
                <PlayerHeaderCard user={profile} setProfile={setProfile} setUser={setUser} winRate={winRate} isOwnProfile={isOwnProfile} setEditing={setEditing} handleShare={handleShare} setShowQr={setShowQr} />

                {/* 2. Stats Grid (Bento Style) */}
                <div className="my-4">
                    <PlayerStatsCards stats={stats} />
                </div>

                {!isOwnProfile && user && (
                    <div className="mb-4">
                        {/* Section Label - Uses border-secondary-subtle for theme awareness */}
                        <div className="px-2 mb-2 d-flex align-items-center gap-2">
                            <span className="text-muted fw-bold text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '1px' }}>
                                Your Rivalry
                            </span>
                            <div className="flex-grow-1 border-bottom border-secondary-subtle opacity-25"></div>
                        </div>

                        <div className="row g-2">
                            {/* 1. Against (Rivals) Card */}
                            <div className="col-6">
                                <div className="card border-0 shadow-sm h-100 bg-body-tertiary"
                                    style={{ borderRadius: '24px' }}>
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            {/* The 'Against' icon uses a fixed red with low opacity that looks great in both modes */}
                                            <div className="rounded-circle d-flex align-items-center justify-content-center"
                                                style={{ width: '24px', height: '24px', fontSize: '0.8rem', backgroundColor: 'rgba(239, 68, 68, 0.15)', color: '#EF4444' }}>
                                                ⚔️
                                            </div>
                                            <span className="text-muted fw-bold" style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>Against</span>
                                        </div>

                                        <div className="d-flex align-items-end gap-1 mb-1">
                                            <h4 className="fw-black mb-0 text-body" style={{ fontSize: '1.4rem' }}>
                                                {h2hStats.vsWins}-{h2hStats.vsLosses}
                                            </h4>
                                        </div>
                                        <div className="text-muted" style={{ fontSize: '0.6rem', fontWeight: '700' }}>
                                            {vsWinRate}% Win Rate
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 2. Paired (Teammates) Card */}
                            <div className="col-6">
                                <div className="card border-0 shadow-sm h-100 bg-body-tertiary"
                                    style={{ borderRadius: '24px' }}>
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            {/* The 'Paired' icon uses your THEME_COLOR with transparency */}
                                            <div className="rounded-circle d-flex align-items-center justify-content-center"
                                                style={{ width: '24px', height: '24px', fontSize: '0.8rem', backgroundColor: `${THEME_COLOR}25`, color: THEME_COLOR }}>
                                                🤝
                                            </div>
                                            <span className="text-muted fw-bold" style={{ fontSize: '0.65rem', textTransform: 'uppercase' }}>Paired</span>
                                        </div>

                                        <div className="d-flex align-items-end gap-1 mb-1">
                                            <h4 className="fw-black mb-0 text-body" style={{ fontSize: '1.4rem' }}>
                                                {h2hStats.pairedWins}-{h2hStats.pairedLosses}
                                            </h4>
                                        </div>
                                        <div className="text-muted" style={{ fontSize: '0.6rem', fontWeight: '700' }}>
                                            {pairedWinRate}% Efficiency
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Past Sessions */}
                <div>
                    <SessionList
                        title={
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <i className="bi bi-clock-history text-muted"></i>
                                <span className="fw-bold text-muted" style={{ fontSize: '0.9rem' }}>Sessions</span>
                            </div>
                        }
                        sessions={pastJoinedSessions}
                        clubId={CLUB_ID}
                        isPast={true}
                        resolvedUid={resolvedUid}
                        isOwnProfile={isOwnProfile}
                        statsMap={sessionStats}
                    />
                </div>

                {/* Empty State Logic */}
                {pastJoinedSessions.length === 0 && (
                    <div className="text-center py-5">
                        <div className="display-6 opacity-25">🥒🏓</div>
                        <p className="text-muted small mt-2">No club activity found yet.</p>
                    </div>
                )}

            </div>
            {showQr && (
                <>
                    {/* Clean Backdrop: Styled to be slightly frosted/white instead of dark */}
                    <div
                        className="modal-backdrop fade show"
                        onClick={() => setShowQr(false)}
                    />

                    <div
                        className="modal fade show d-block"
                        tabIndex="-1"
                        role="dialog"
                        aria-modal="true"
                        style={{ zIndex: 1055 }}
                    >
                        <div className="modal-dialog modal-dialog-centered px-3" style={{ maxWidth: '400px' }}>
                            <div className="modal-content border-0 shadow-lg rounded-5 overflow-hidden">

                                {/* Header with a Violet tint */}
                                <div className="modal-header border-0 pb-0 pt-4 px-4 d-flex justify-content-between align-items-center">
                                    <h5 className="fw-bold mb-0" >My QR</h5>
                                    <button
                                        type="button"
                                        className="btn-close shadow-none"
                                        onClick={() => setShowQr(false)}
                                    />
                                </div>

                                <div className="modal-body text-center p-4">
                                    <div className="d-flex justify-content-center align-items-center">
                                        <div
                                            className="p-3 rounded-4"
                                            style={{ backgroundColor: "#fff" }}
                                        >
                                            <QRCodeCanvas
                                                size={280} // Reduced size slightly for better mobile fit
                                                value={JSON.stringify({
                                                    uid: user.uid,
                                                    name: user.displayName,
                                                    username: user.username || null,
                                                    photoURL: user.photoURL || null,
                                                    level: user.level || 1,
                                                })}
                                                bgColor={"#ffffff"}
                                                fgColor={THEME_VIOLET}
                                                level={"H"}
                                                imageSettings={{
                                                    src: "/img/rrc2.png",
                                                    height: 45,
                                                    width: 45,
                                                    excavate: true,
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="mt-4">
                                        <p className="text-muted small px-3">
                                            Show this QR to the queue admin
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            {editing && (
                <EditProfileModal
                    user={user}
                    setUser={setUser}
                    profile={profile}
                    onClose={(updated) => {
                        setEditing(false);
                        if (updated) {
                            loadUser();
                            Swal.fire({
                                toast: true,
                                position: 'center',
                                icon: 'success',
                                title: 'Profile updated',
                                showConfirmButton: false,
                                timer: 2000
                            });
                        }
                    }}
                />
            )}
            {user && (
                <BottomNav />
            )}
            <style>{`
                .fw-black { font-weight: 900; }
                .tracking-widest { letter-spacing: 0.1em; }
            `}</style>
        </div>
    );
}